import {
  type BidV2Dto,
  type BidV2PatchDto,
  type MinimumRequirementDto,
  type SupplierBidV2MinimumRequirementResponseDto,
} from "../../../../autogen/bff-api";
import { useEditor } from "../../../../common/editing/save-coordinator";
import {
  isMinimumRequirementResponseComplete,
  minimumRequirementAnswerStatus,
} from "./MinimumRequirementAnswerCard";
import { isBidV2Editable } from "./publicBidEditability";

/**
 * The supplier's side of the minimum requirements, shared by the two delivery surfaces: the public
 * bid wizard's step and the private bid page (SM-952 gave private procurements minimum requirements
 * too). Only the chrome differs between them — the answer semantics live here.
 */
export const useMinimumRequirementAnswers = (bidId: string) => {
  const { state, updateField, fieldStatus } = useEditor<BidV2Dto, BidV2PatchDto>(bidId);
  const responses = state.minimumRequirementResponses ?? [];

  const responseFor = (requirement: MinimumRequirementDto) =>
    responses.find((response) => response.minimumRequirementId === (requirement.id ?? ""));

  const upsert = (
    requirement: MinimumRequirementDto,
    patch: Partial<SupplierBidV2MinimumRequirementResponseDto>,
  ) => {
    const id = requirement.id ?? "";
    const current = responseFor(requirement);
    const next: SupplierBidV2MinimumRequirementResponseDto = {
      minimumRequirementId: id,
      answer: current?.answer ?? false,
      comment: current?.comment ?? null,
      attachmentIds: current?.attachmentIds ?? current?.attachments?.map((document) => document.id) ?? [],
      ...patch,
    };
    updateField(
      { minimumRequirementResponses: [...responses.filter((r) => r.minimumRequirementId !== id), next] },
      { debounceMs: 300 },
    );
  };

  return {
    responseFor,
    upsert,
    answeredCount: (requirements: MinimumRequirementDto[]) =>
      requirements.filter((requirement) =>
        isMinimumRequirementResponseComplete(requirement, responseFor(requirement)),
      ).length,
    notMetCount: (requirements: MinimumRequirementDto[]) =>
      requirements.filter(
        (requirement) => minimumRequirementAnswerStatus(requirement, responseFor(requirement)) === "not-met",
      ).length,
    commentRequiredCount: (requirements: MinimumRequirementDto[]) =>
      requirements.filter(
        (requirement) =>
          minimumRequirementAnswerStatus(requirement, responseFor(requirement)) === "comment-required",
      ).length,
    isSaving:
      fieldStatus("minimumRequirementResponses") === "saving" ||
      fieldStatus("minimumRequirementResponses") === "pending",
    readOnly: !isBidV2Editable(state),
  };
};
